import React, { useCallback, useEffect, useMemo, useState } from "react";

import useProjectContext from "@/services/context/project/use-project-context";
import useProjectAccess from "@/hooks/use-project-access.ts";
import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context.ts";

import { DropDownItem } from "@/models/interfaces/drop-down-item.ts";
import DropdownSelector from "@/components/dropdown-selector";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import Button from "@/components/buttons/button";

export interface ValidationReviewProps {
    onValidate: (selectedRevision: DropDownItem<number>, isNewRevision: boolean) => void;
    onCancel: () => void;
}

const ValidationReview: React.FC<ValidationReviewProps> = ({
    onValidate,
    onCancel,
}) => {
    const { project } = useProjectContext();
    const { isLoading } = useValidationContext();

    const revNumber: number = useMemo(() => {
        if (project == null || project.boomerRevisionNumber == null)
            return 0;
        return Number(project.boomerRevisionNumber);
    }, [project]);

    const listBoxItems: DropDownItem<number>[] = useMemo(() => {
        const items: DropDownItem<number>[] = [];
        if (project) {
            if (project.boomerRevisionNumber) {
                const incr = revNumber + 1;
                items.push({
                    display: `Révision ${revNumber}`,
                    key: project.boomerRevisionNumber,
                    value: revNumber
                });
                items.push({ display: `Révision ${incr}`, key: incr, value: incr });
            } else {
                items.push({ display: `Révision 1`, key: 1, value: 1 });
            }
        }
        return items;
    }, [project, revNumber]);

    const [selectedRevision, setSelectedRevision] = useState(listBoxItems[0]);
    const [text, setText] = useState("");

    const {
        accessQueryResult
    } = useProjectAccess(project.id as string);

    const onUpdateClick = useCallback(() => {
        const isNewRevision = selectedRevision.value !== listBoxItems[0]?.value || revNumber === 0;
        onValidate(selectedRevision, isNewRevision);
    }, [onValidate, selectedRevision, listBoxItems, revNumber]);

    useEffect(() => {
        if (accessQueryResult.data === false) {
            setText("Vous n'avez pas les permissions pour pouvoir créer une nouvelle révision.");
            return;
        }

        if (selectedRevision.value === listBoxItems[0].value) {
            setText("Veuillez noter que si vous continuez, la révision actuelle sera remplacée par les nouvelles valeurs indiquées.");
        } else {
            setText("");
        }
    }, [accessQueryResult, selectedRevision, listBoxItems]);
    
    if(isLoading)
        return <SimpleLoadingIndicator />;

    return (
        <div className='flex flex-col gap-y-2'>
            {revNumber > 0 &&
                <div>
                    <div className='flex flex-row items-center gap-x-2'>
                        <div className="block text-sm font-medium text-gray-900">Révision</div>
                        <DropdownSelector items={listBoxItems} selectedItem={selectedRevision}
                                          onSelectionChanged={e => {
                                              if (e)
                                                  setSelectedRevision(e);
                                          }}
                                          keySelector={i => i.key}
                                          displaySelector={i => i.display}/>
                    </div>
                    <span className="text-sm text-rose-400">{text}</span>
                </div>
            }
            <div className='flex flex-row flex-1 gap-x-2 items-center'>
                <Button size='md' variant='simple' onClick={onCancel}>
                    <span>Annuler</span>
                </Button>

                <Button className='ml-auto' size="md"
                        disabled={accessQueryResult.data !== true}
                        onClick={() => onUpdateClick()}>
                    {selectedRevision.value === listBoxItems[0].value && revNumber > 0
                        ? "Mettre à jour la révision"
                        : "Créer une nouvelle révision"}
                </Button>
            </div>
        </div>
    )
}

export default ValidationReview;